/**
 * NotificationCenter — visual components for Caisse Pro notifications.
 *
 * Exports:
 *   - NotifBadge        : small animated badge used on tabs
 *   - NotificationBell  : large profile-level bell + dropdown with mark-as-read
 *   - CrossRoleBanner   : full-width pulsing banner sticky under the header
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Bell, CheckCircle, ChevronRight, X } from "lucide-react";
import {
  COUNT_META,
  COLOR_BG,
  COLOR_BADGE,
  formatRelativeTime,
} from "../utils/notifications";

export const NotifBadge = ({ count, color = "red", testid }) => {
  if (!count || count <= 0) return null;
  const cls = COLOR_BADGE[color] || COLOR_BADGE.red;
  return (
    <span className="relative inline-flex ml-1" data-testid={testid}>
      <span className={`absolute inset-0 rounded-full ${cls} opacity-75 animate-ping`} />
      <span className={`relative inline-flex items-center justify-center rounded-full text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] ${cls}`}>
        {count > 99 ? "99+" : count}
      </span>
    </span>
  );
};

export const NotificationBell = ({
  effectiveCounts,
  effectiveTotal,
  notifLatest,
  showNotifCenter,
  setShowNotifCenter,
  onOpenNotif,
  onMarkAllRead,
}) => {
  return (
    <div className="relative" data-testid="notif-center">
      <Button
        variant="ghost"
        onClick={() => setShowNotifCenter((s) => !s)}
        className="relative px-2 text-slate-200 hover:bg-slate-700/40"
        data-testid="notif-center-btn"
        title={effectiveTotal > 0 ? `${effectiveTotal} notification(s)` : "Aucune notification"}
      >
        <Bell className="w-6 h-6" />
      </Button>
      {showNotifCenter && (
        <>
          <div
            className="fixed inset-0 z-[90]"
            onClick={() => setShowNotifCenter(false)}
            data-testid="notif-center-backdrop"
          />
          <div
            className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-1rem)] bg-slate-800 border border-slate-700 rounded-lg shadow-2xl z-[100] overflow-hidden"
            data-testid="notif-center-dropdown"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 py-2 bg-slate-900/50 border-b border-slate-700">
              <span className="text-slate-200 font-semibold text-sm flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-300" /> Notifications
              </span>
              {effectiveTotal > 0 && (
                <button
                  type="button"
                  onClick={onMarkAllRead}
                  className="text-xs text-slate-300 hover:text-white bg-slate-700/60 hover:bg-slate-600 px-2 py-1 rounded cursor-pointer"
                  data-testid="notif-mark-all-read"
                >
                  Tout marquer lu
                </button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-slate-700/60">
              {effectiveTotal === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400 opacity-70" />
                  Aucune notification en attente
                </div>
              ) : (
                Object.entries(effectiveCounts)
                  .filter(([, v]) => (Number(v) || 0) > 0)
                  .sort((a, b) => (b[1] || 0) - (a[1] || 0))
                  .map(([key, count]) => {
                    const meta = COUNT_META[key] || { color: "red", label: key };
                    const color = COLOR_BG[meta.color] || COLOR_BG.slate;
                    const rel = formatRelativeTime(notifLatest?.[key]);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => onOpenNotif(key)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-700/40 active:bg-slate-700/60 flex items-center justify-between gap-2 group cursor-pointer"
                        data-testid={`notif-item-${key}`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className={`w-2 h-2 rounded-full ${color} shrink-0`} />
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-slate-200 text-sm truncate">{meta.label}</span>
                            {rel && (
                              <span className="text-[11px] text-slate-500 truncate" data-testid={`notif-item-${key}-ts`}>{rel}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`${color} text-white text-xs font-bold rounded-full px-2 min-w-[24px] text-center`}>
                            {count}
                          </span>
                          <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-300" />
                        </div>
                      </button>
                    );
                  })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const CrossRoleBanner = ({ crossRole, role, onOpenLatest, onDismiss }) => {
  if (!crossRole || crossRole.total <= 0) return null;
  if (role !== "admin" && role !== "manager") return null;

  // On garde les 2 items les plus importants uniquement pour la lisibilité
  const topItems = Object.entries(crossRole.items)
    .filter(([, v]) => v.count > 0)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 2);

  const accent = role === "manager" ? "#f59e0b" : "#10b981";

  return (
    <div
      className="crb-root sticky top-[60px] z-[80] w-full border-b bg-slate-900/95 backdrop-blur-md"
      style={{ borderBottomColor: `${accent}55` }}
      data-testid="cross-role-banner"
    >
      <style>{`
        .crb-root, .crb-root * { font-family: 'Space Grotesk', 'Outfit', system-ui, sans-serif; }
        .crb-count {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 22px; height: 22px; padding: 0 6px;
          border-radius: 6px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px; font-weight: 700;
          background: var(--accent);
          color: rgb(15, 23, 42);
        }
        .crb-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 2px 8px; border-radius: 999px;
          font-size: 11px; font-weight: 600;
          background: rgba(51, 65, 85, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.4);
          color: rgb(203, 213, 225);
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-3 py-2 flex items-center gap-2.5" style={{ ["--accent"]: accent }}>
        <button
          type="button"
          onClick={onOpenLatest}
          className="flex items-center gap-2.5 flex-1 min-w-0 text-left hover:opacity-80 transition"
          data-testid="cross-role-banner-link"
        >
          <span className="crb-count" style={{ background: accent }}>
            {crossRole.total > 99 ? "99+" : crossRole.total}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-slate-200 text-[12.5px] font-semibold leading-tight truncate">
              <span style={{ color: accent }}>{crossRole.source_label}</span>
              <span className="text-slate-400"> · nouvelles infos</span>
            </p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {topItems.map(([k, v]) => (
                <span key={k} className="crb-pill">
                  <span className="font-mono font-bold" style={{ color: accent }}>{v.count}</span>
                  <span className="text-slate-400 truncate">{(COUNT_META[k]?.label || k).toLowerCase()}</span>
                </span>
              ))}
              {crossRole.latest_timestamp && (
                <span className="text-slate-500 text-[10.5px]">{formatRelativeTime(crossRole.latest_timestamp)}</span>
              )}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" strokeWidth={2.4} />
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 w-7 h-7 rounded-md text-slate-500 hover:text-white hover:bg-slate-700/60 flex items-center justify-center"
          title="Masquer"
          data-testid="cross-role-banner-dismiss"
        >
          <X className="w-4 h-4" strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
};
